/**
 * FAQ content for the /faq route.
 * Lifted verbatim from the original artifact template's FAQ constant.
 */

export interface FaqEntry {
  /** Grouping tag: data | panels | engine | record | legal | setup | ui | access */
  tag: string;
  q: string;
  a: string;
}

export const FAQ: FaqEntry[] = [
  {
    tag: 'data',
    q: 'Where does the data come from?',
    a: 'Free and keyless: CoinGecko, GDELT, RSS wires, Frankfurter FX, ECB, World Bank, IMF, OECD and the US Treasury. Free with a key: FRED, and SEC EDGAR which needs only a contact string. Paid and optional: Polygon, Finnhub and Twelve Data. Without the paid ones those panels are honestly empty, which is a supported state rather than a broken one.',
  },
  {
    tag: 'panels',
    q: 'What happens to a panel with no connector?',
    a: 'It renders its real chrome — real title, real header, real source strip — over a hatched area naming the exact module that would feed it, plus what that module would have to supply. It is never a loading shimmer: a shimmer implies arriving shortly, which is a small lie when no code exists yet.',
  },
  {
    tag: 'engine',
    q: 'Is it just TimesFM?',
    a: 'No. TimesFM 2.5 produces the quantile forecast for a single series, and the GodEngine swarm resolver produces branching scenarios with probability mass, assumptions and triggers. Both are the engine; they answer different questions and feed different sections — Forecast Lab and Record on one side, Scenarios, Simulation and Regimes on the other.',
  },
  {
    tag: 'engine',
    q: 'Is the forecast a black box?',
    a: 'The decoder name travels to the browser with every forecast, alongside the horizon, the quantiles, the baseline it is scored against and the as-of stamp of its input series. When weights are missing the loader falls back to a polyfit stub, and a forecast from that fallback draws no line at all.',
  },
  {
    tag: 'record',
    q: 'Why are the scores empty?',
    a: 'Because no forecast has resolved yet in that deployment. Scores read as em dashes rather than zeros, and pending forecasts are counted separately from resolved ones so the sample size is never quietly inflated. A backtested number says in the panel that it is backtested.',
  },
  {
    tag: 'legal',
    q: 'Can I re-serve the data through my own API?',
    a: 'Only where the provider allows it. Each connector records its redistribution terms; a source marked non-redistributable is display-only and is excluded from the public API and from exports, and copy and export are disabled on panels fed only by such sources.',
  },
  {
    tag: 'setup',
    q: 'Why are the ports unusual?',
    a: 'Postgres 5433, Redis 6380, API 8110 and frontend 3100 — deliberately not the defaults, because the same machine may also run god-engine\u2019s stack and a silent connection to the wrong database is the worst available failure mode.',
  },
  {
    tag: 'setup',
    q: 'Why is the frontend not in Docker?',
    a: 'Watching the filesystem through a container sharing layer turns a one-second edit loop into a ten-second one, and the forecast worker loads a ~925 MB model already sitting on local disk. Postgres and Redis run in Docker; the API, workers and frontend run natively.',
  },
  {
    tag: 'ui',
    q: 'Does it work on a phone?',
    a: 'The relaxed density doubles as the touch profile with 44px targets, and panels that cannot be legible below a given width say so rather than shrinking. Correlation matrices, world maps and linkage graphs are desktop-only by declaration, not by accident.',
  },
  {
    tag: 'access',
    q: 'Is there a hosted version I can log into?',
    a: 'Not yet. Authentication has no endpoint in the repository, so the login screen on this site is the design for it and gates the demo in the browser only. Self-hosting is the supported path today and is documented end to end.',
  },
  {
    tag: 'ui',
    q: 'Why two themes?',
    a: 'Terminal is the dark theme the product is designed in. Canon is a light remap of the same token names for people who work in bright rooms or print screenshots. Both are stamped before first paint, so switching never flashes the wrong one.',
  },
  {
    tag: 'engine',
    q: 'What stops a fake number slipping in?',
    a: 'Four guards: every data component must accept an empty state at compile time, unavailable endpoints must return a null payload rather than an empty list, sample-data libraries are banned outside the test tree, and a fallback-decoder forecast draws nothing. None of them depend on a reviewer remembering.',
  },
];
